import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  Bell,
  Calendar,
  HeartPulse,
  LayoutDashboard,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import InsightsPageShell from "../../components/MasterAdmin/insights/InsightsPageShell";
import InsightsStatCard from "../../components/MasterAdmin/insights/InsightsStatCard";
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from "../../components/MasterAdmin/insights/InsightsChartCard";
import ActivityTrackingPanel from "../../components/MasterAdmin/insights/ActivityTrackingPanel";
import {
  herPhasesAnalyticsService,
  type HerPhasesOverview,
  type HerPhasesUser,
  type UserCalculationHistory,
} from "../../services/herPhases/herPhasesAnalyticsService";
import { herPhasesMetricInfo } from "../../config/masterAdminMetricDefinitions";
import {
  CHART_COLORS,
  PIE_PALETTE,
  opsFilterClass,
  opsMetricGridClass,
  opsPrimaryBtn,
  opsTableWrap,
} from "../../components/MasterAdmin/operations/operationsUi";

type TabId = "overview" | "calculators" | "cycle" | "users" | "activity" | "outreach";

const TABS: Array<{ id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "calculators", label: "Calculators", icon: BarChart3 },
  { id: "cycle", label: "Cycle insights", icon: HeartPulse },
  { id: "users", label: "User activity", icon: Users },
  { id: "activity", label: "Product activity", icon: Activity },
  { id: "outreach", label: "Outreach", icon: Bell },
];

const TAB_META: Record<TabId, { title: string; description: string }> = {
  overview: {
    title: "Her Phases overview",
    description: "Calculator usage, cycle health, and product opens from MySQL, Firebase events, and BigQuery.",
  },
  calculators: {
    title: "Calculator mix",
    description: "How Period, Ovulation, Pregnancy Test, and Menstrual Cycle calculators are used.",
  },
  cycle: {
    title: "Cycle insights",
    description: "Cycle length, period duration, and typical vs outlier patterns from saved calculations.",
  },
  users: {
    title: "User activity",
    description: "People who ran Her Phases calculators, with calculation history per user.",
  },
  activity: {
    title: "Product activity",
    description: "Screen views, opens, sessions, and visitors for Her Phases from BigQuery (Firebase / app / web events).",
  },
  outreach: {
    title: "WhatsApp & retargeting",
    description: "Her Phases nudges queued after HER_PHASES_OPENED events.",
  },
};

const CHANNEL_LABEL: Record<string, string> = {
  web: "Website",
  user_app: "User app",
  plus_app: "Plus app",
};

const CALCULATOR_COLORS: Record<string, string> = {
  PERIOD: "#ec4899",
  OVULATION: "#10b981",
  PREGNANCY_TEST: "#f59e0b",
  MENSTRUAL_CYCLE: "#2563eb",
};

function addDays(base: Date, days: number) {
  return new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
}

function formatDate(date: Date | null): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDate(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTick(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function calculatorLabel(type?: string | null) {
  if (!type) return "Unknown";
  if (type === "PERIOD") return "Period calculator";
  if (type === "OVULATION") return "Ovulation calculator";
  if (type === "PREGNANCY_TEST") return "Pregnancy test calculator";
  if (type === "MENSTRUAL_CYCLE") return "Menstrual cycle calculator";
  return type.replace(/_/g, " ");
}

const HerPhasesAnalyticsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(addDays(new Date(), -29));
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [dateFilter, setDateFilter] = useState("last30");
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<HerPhasesOverview | null>(null);

  const [users, setUsers] = useState<HerPhasesUser[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [search, setSearch] = useState("");
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [history, setHistory] = useState<UserCalculationHistory[]>([]);
  const [historyUser, setHistoryUser] = useState<{
    user_name?: string;
    totalCalculations?: number;
    phone_number?: string;
    email_id?: string;
  } | null>(null);
  const [calculatorUsageSummary, setCalculatorUsageSummary] = useState<Record<string, number> | null>(null);

  const loadOverview = useCallback(async (from = fromDate, to = toDate) => {
    if (!from || !to) return;
    try {
      setLoading(true);
      const overviewResponse = await herPhasesAnalyticsService.getOverview(from, to);
      setOverview(overviewResponse.data);
    } catch (error) {
      console.error("Failed to load analytics", error);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const loadUsers = useCallback(async (query = search, nextPage = page) => {
    try {
      const usersResponse = await herPhasesAnalyticsService.getUsers(query, nextPage, 10);
      setUsers(usersResponse.data.users || []);
      setTotalPages(usersResponse.data.totalPages || 1);
      setTotalUsersCount(usersResponse.data.total || 0);
      setPage(nextPage);
    } catch (error) {
      console.error("Failed to load users", error);
    }
  }, [search, page]);

  const loadUserHistory = async (userId: string) => {
    try {
      const response = await herPhasesAnalyticsService.getUserCalculationHistory(userId);
      setHistoryUser(response.data.user);
      setCalculatorUsageSummary(response.data.usage);
      setHistory(response.data.history || []);
      setShowHistoryDrawer(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    const today = new Date();
    let from = new Date(today);
    let to = new Date(today);
    switch (value) {
      case "today":
        break;
      case "yesterday":
        from.setDate(today.getDate() - 1);
        to.setDate(today.getDate() - 1);
        break;
      case "last7":
        from.setDate(today.getDate() - 6);
        break;
      case "last30":
        from.setDate(today.getDate() - 29);
        break;
      case "thisMonth":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "lastMonth":
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "custom":
        return;
      default:
        break;
    }
    setFromDate(from);
    setToDate(to);
  };

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeTab === "users") void loadUsers(search, 1);
  }, [activeTab]);

  const stackedCalculatorTrend = useMemo(() => {
    const byDate: Record<string, Record<string, string | number>> = {};
    (overview?.dailyByCalculator || []).forEach((row) => {
      const key = String(row.date);
      if (!byDate[key]) byDate[key] = { date: key };
      byDate[key][row.calculator_type] = row.count;
    });
    return Object.values(byDate);
  }, [overview?.dailyByCalculator]);

  const typicalCycleShare = useMemo(() => {
    const buckets = overview?.cycleLengthBuckets || [];
    const total = buckets.reduce((sum, row) => sum + row.count, 0);
    const typical = buckets
      .filter((row) => row.key === "typical" || row.key === "shorter_typical")
      .reduce((sum, row) => sum + row.count, 0);
    return total > 0 ? ((typical / total) * 100).toFixed(1) : "0.0";
  }, [overview?.cycleLengthBuckets]);

  return (
    <InsightsPageShell
      title={TAB_META[activeTab].title}
      description={TAB_META[activeTab].description}
      info={herPhasesMetricInfo.page}
      actions={
        <>
          <select
            value={dateFilter}
            onChange={(e) => handleDateFilterChange(e.target.value)}
            className={opsFilterClass}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="last30">Last 30 days</option>
            <option value="thisMonth">This month</option>
            <option value="lastMonth">Last month</option>
            <option value="custom">Custom</option>
          </select>
          <label className={`${opsFilterClass} flex items-center gap-2`}>
            <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              type="date"
              value={formatDate(fromDate)}
              onChange={(e) => {
                setDateFilter("custom");
                setFromDate(parseDate(e.target.value));
              }}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            />
          </label>
          <label className={`${opsFilterClass} flex items-center gap-2`}>
            <input
              type="date"
              value={formatDate(toDate)}
              onChange={(e) => {
                setDateFilter("custom");
                setToDate(parseDate(e.target.value));
              }}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            />
          </label>
          <button type="button" onClick={() => void loadOverview()} disabled={loading} className={opsPrimaryBtn}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </>
      }
    >
      <div className="flex w-full items-center gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900/60">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-white text-primary-600 shadow-sm dark:bg-gray-800"
                  : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          {loading && !overview ? (
            <div className="space-y-4">
              <div className={opsMetricGridClass}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
                ))}
              </div>
            </div>
          ) : overview ? (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setActiveTab("activity")}
                  className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Product opens</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {Number(overview.engagement?.summary?.unique_visitors || 0).toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    unique visitors · {Number(overview.engagement?.summary?.total_events || 0).toLocaleString()} events
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("calculators")}
                  className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Calculations</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {overview.totalCalculations.toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {overview.mostUsedCalculatorLabel || calculatorLabel(overview.mostUsedCalculator)} most used
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("outreach")}
                  className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Retarget nudges</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {Number(overview.outreach?.total || 0).toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">WhatsApp queue for Her Phases</p>
                </button>
              </div>

              <div className={opsMetricGridClass}>
                <InsightsStatCard
                  label="Calculator users"
                  value={overview.totalUsers.toLocaleString()}
                  sub="Unique user IDs in MySQL herPhases"
                  icon={Users}
                  info={herPhasesMetricInfo.totalUsers}
                />
                <InsightsStatCard
                  label="Calcs / user"
                  value={overview.averageCalculationsPerUser}
                  sub={`${overview.returningUsers} returning users`}
                  icon={BarChart3}
                  info={herPhasesMetricInfo.calcsPerUser}
                />
                <InsightsStatCard
                  label="Avg cycle length"
                  value={`${overview.averageCycleLength}d`}
                  sub={`Typical band avg ${overview.averageCycleLengthHealthy}d`}
                  icon={HeartPulse}
                  info={herPhasesMetricInfo.cycleLength}
                />
                <InsightsStatCard
                  label="Avg period duration"
                  value={`${overview.averagePeriodDuration}d`}
                  sub={`${typicalCycleShare}% of cycles in 21–35 days`}
                  icon={HeartPulse}
                  info={herPhasesMetricInfo.periodDuration}
                />
                <InsightsStatCard
                  label="Hub → calculator"
                  value={`${overview.conversion?.hubToCalculatorRate ?? 0}%`}
                  sub={`${overview.conversion?.calculatorOpens || 0} calculator opens`}
                  icon={Activity}
                />
                <InsightsStatCard
                  label="Calculator → result"
                  value={`${overview.conversion?.calculatorToResultRate ?? 0}%`}
                  sub={`${overview.conversion?.resultsViewed || 0} results viewed`}
                  icon={HeartPulse}
                />
                <InsightsStatCard
                  label="Open → calculate"
                  value={`${overview.conversion?.openToCalculateRate ?? 0}%`}
                  sub={`${overview.conversion?.calculations || 0} calcs vs ${overview.conversion?.herPhasesOpened || overview.conversion?.visitors || 0} opens`}
                  icon={Activity}
                  info={herPhasesMetricInfo.conversion}
                />
                <InsightsStatCard
                  label="Guest calculations"
                  value={overview.guestCalculations.toLocaleString()}
                  sub={`${overview.registeredUsers} logged-in users`}
                  icon={Users}
                  info={herPhasesMetricInfo.guests}
                />
                <InsightsStatCard
                  label="Repeat rate"
                  value={`${overview.repeatRate}%`}
                  sub="Logged-in users with more than one calculation"
                  icon={RefreshCw}
                  info={herPhasesMetricInfo.repeatRate}
                />
                <InsightsStatCard
                  label="Avg dwell"
                  value={`${Number(overview.engagement?.summary?.avg_dwell_sec || 0).toFixed(0)}s`}
                  sub={`${Number(overview.engagement?.summary?.sessions || 0).toLocaleString()} sessions`}
                  icon={Activity}
                  info={herPhasesMetricInfo.dwell}
                />
              </div>

              <InsightsChartCard title="Usage funnel (events + saved calculations)" heightClass="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { step: "Hub opens", count: overview.conversion?.herPhasesOpened || 0 },
                      { step: "Calculator opens", count: overview.conversion?.calculatorOpens || 0 },
                      { step: "Calculate tapped", count: overview.conversion?.calculateClicks || 0 },
                      { step: "Result viewed", count: overview.conversion?.resultsViewed || 0 },
                      { step: "Saved in MySQL", count: overview.conversion?.calculations || 0 },
                    ]}
                    layout="vertical"
                    margin={{ left: 24, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <YAxis type="category" dataKey="step" width={120} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                    <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </InsightsChartCard>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <InsightsChartCard title="Daily calculations" info={herPhasesMetricInfo.dailyTrend} className="xl:col-span-7" heightClass="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview.dailyTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={formatTick} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} labelFormatter={(v) => formatTick(String(v))} />
                      <Area type="monotone" dataKey="calculations" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </InsightsChartCard>
                <InsightsChartCard title="Calculator mix" info={herPhasesMetricInfo.calculatorMix} className="xl:col-span-5" heightClass="h-72">
                  {(overview.calculatorUsage || []).length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-gray-500">No calculations in this range.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={overview.calculatorUsage} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={55} outerRadius={90}>
                          {(overview.calculatorUsage || []).map((row, index) => (
                            <Cell key={row.calculator_type} fill={CALCULATOR_COLORS[row.calculator_type] || PIE_PALETTE[index % PIE_PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </InsightsChartCard>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <InsightsChartCard title="Channel mix (BigQuery events)" heightClass="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={(overview.engagement?.by_channel || []).map((row) => ({
                        ...row,
                        label: CHANNEL_LABEL[row.channel] || row.channel,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                      <Bar dataKey="events" name="Events" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </InsightsChartCard>
                <InsightsChartCard title="When people calculate" heightClass="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={overview.hourlyTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={2} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                      <Bar dataKey="calculations" name="Calculations" fill={CHART_COLORS.cyan} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </InsightsChartCard>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No Her Phases data for this range.</p>
          )}
        </div>
      )}

      {activeTab === "calculators" && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(overview.calculatorUsage || []).map((row) => (
              <InsightsStatCard
                key={row.calculator_type}
                label={row.label || calculatorLabel(row.calculator_type)}
                value={row.count.toLocaleString()}
                sub={`${overview.totalCalculations > 0 ? ((row.count / overview.totalCalculations) * 100).toFixed(1) : 0}% of calculations`}
              />
            ))}
          </div>
          <InsightsChartCard title="Calculator usage over time" heightClass="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stackedCalculatorTrend}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatTick} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Legend />
                {["PERIOD", "OVULATION", "PREGNANCY_TEST", "MENSTRUAL_CYCLE"].map((key) => (
                  <Line key={key} type="monotone" dataKey={key} name={calculatorLabel(key)} stroke={CALCULATOR_COLORS[key]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </InsightsChartCard>
          <InsightsChartCard title="Weekday pattern" heightClass="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overview.weekdayTrend || []}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="weekday" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Bar dataKey="calculations" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </InsightsChartCard>
        </div>
      )}

      {activeTab === "cycle" && overview && (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            <InsightsStatCard label="Average cycle" value={`${overview.averageCycleLength} days`} sub="All entered cycle lengths" />
            <InsightsStatCard label="Typical-band average" value={`${overview.averageCycleLengthHealthy} days`} sub="Restricted to 21–35 days" />
            <InsightsStatCard label="Average period" value={`${overview.averagePeriodDuration} days`} sub="Entered period duration" />
            <InsightsStatCard label="Cycles in 21–35 days" value={`${typicalCycleShare}%`} sub="Share of calculations" />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <InsightsChartCard title="Cycle length distribution" heightClass="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.cycleLengthBuckets || []}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                  <Bar dataKey="count" fill="#ec4899" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </InsightsChartCard>
            <InsightsChartCard title="Period duration distribution" heightClass="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.periodDurationBuckets || []}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </InsightsChartCard>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            Her Phases estimates next period, ovulation, fertile window, follicular and luteal phases from last period date and cycle length.
            Short or long cycles here are input patterns, not diagnoses.
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search name, phone, email, or user id"
              value={search}
              onChange={(e) => {
                const value = e.target.value;
                setSearch(value);
                void loadUsers(value, 1);
              }}
              className={`${opsFilterClass} w-full pl-10`}
            />
          </div>
          <div className={opsTableWrap}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  {["User", "Phone", "Email", "Calcs", "Mix", "Last activity", ""].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
                {users.map((user, index) => (
                  <tr key={`${user.user_id || "guest"}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{user.user_name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{user.phone_number || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{user.email_id || "—"}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">{user.totalCalculations}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                      P {user.periodCount || 0} · O {user.ovulationCount || 0} · T {user.pregnancyTestCount || 0} · C {user.menstrualCycleCount || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDateTime(user.lastActivity)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => void loadUserHistory(user.user_id)}
                        className="text-xs font-medium text-primary-600 hover:underline"
                      >
                        History
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <p>
              Page {page} of {totalPages} ({totalUsersCount} users)
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => void loadUsers(search, page - 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-50 dark:border-gray-700"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => void loadUsers(search, page + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-50 dark:border-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "activity" && <ActivityTrackingPanel fromDate={fromDate} toDate={toDate} lockedProduct="her_phases" />}

      {activeTab === "outreach" && (
        <div className="space-y-4">
          {!overview?.outreach?.available ? (
            <p className="text-sm text-gray-500">{overview?.outreach?.error || "Retargeting data is not available."}</p>
          ) : (
            <>
              <div className={opsMetricGridClass}>
                <InsightsStatCard label="Queued nudges" value={overview.outreach.total.toLocaleString()} sub="WhatsAppRetargetingQueues · HER_PHASES" />
                {(overview.outreach.byStatus || []).map((row) => (
                  <InsightsStatCard key={row.status} label={row.status} value={row.count.toLocaleString()} />
                ))}
              </div>
              <div className={opsTableWrap}>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      {["User", "Event", "Status", "Template", "Send at", "Sent"].map((heading) => (
                        <th key={heading} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
                    {(overview.outreach.recent || []).map((row, index) => (
                      <tr key={`${row.userId}-${index}`}>
                        <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-200">{row.userId}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{row.eventType}</td>
                        <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white">{row.status}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{row.templateName || "—"}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{formatDateTime(row.sendAt)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{formatDateTime(row.sentAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {showHistoryDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-xl dark:bg-gray-900">
            <div className="border-b border-gray-200 p-5 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Calculation history</h2>
                  <p className="mt-1 text-sm font-medium text-gray-800 dark:text-gray-200">{historyUser?.user_name}</p>
                  <p className="text-xs text-gray-500">{historyUser?.phone_number || historyUser?.email_id || ""}</p>
                </div>
                <button type="button" onClick={() => setShowHistoryDrawer(false)} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Period", calculatorUsageSummary?.PERIOD],
                  ["Ovulation", calculatorUsageSummary?.OVULATION],
                  ["Pregnancy test", calculatorUsageSummary?.PREGNANCY_TEST],
                  ["Menstrual cycle", calculatorUsageSummary?.MENSTRUAL_CYCLE],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                    <p className="text-gray-500">{label}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{Number(value || 0)}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4 p-5">
              {history.length === 0 ? (
                <p className="text-sm text-gray-500">No calculations found.</p>
              ) : (
                history.map((item, index) => (
                  <div key={index} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-semibold text-primary-600">{calculatorLabel(item.calculator_type)}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(item.createdAt)}</p>
                    </div>
                    <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {item.cycle_length != null && <p>Cycle length: {item.cycle_length} days</p>}
                      {item.period_duration != null && <p>Period duration: {item.period_duration} days</p>}
                      {item.last_period_date && <p>Last period: {item.last_period_date}</p>}
                      {item.next_period_date && <p>Next period: {item.next_period_date}</p>}
                      {item.ovulation_date && <p>Ovulation: {item.ovulation_date}</p>}
                      {(item.fertile_window_start || item.fertile_window_end) && (
                        <p>
                          Fertile window: {item.fertile_window_start} – {item.fertile_window_end}
                        </p>
                      )}
                      {item.pregnancy_test_date && <p>Pregnancy test date: {item.pregnancy_test_date}</p>}
                      {(item.follicular_phase_start || item.follicular_phase_end) && (
                        <p>
                          Follicular: {item.follicular_phase_start} – {item.follicular_phase_end}
                        </p>
                      )}
                      {(item.luteal_phase_start || item.luteal_phase_end) && (
                        <p>
                          Luteal: {item.luteal_phase_start} – {item.luteal_phase_end}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </InsightsPageShell>
  );
};

export default HerPhasesAnalyticsPage;
